import axios, { AxiosInstance } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  DashboardStats,
  User,
  Event,
  ExhibitorDetails,
  UserFilterParams,
  EventFilterParams,
  GuestSpeaker,
  ExhibitorFilterParams,
  UpdateUserRequest,
  CreateEventRequest,
  UpdateEventRequest,
  BulkUpdateRequest,
  PaginatedResponse,
  ApiResponse,
  Role,
  Permission,
  CreateRoleRequest,
  UpdateRoleRequest,
  AssignRoleRequest,
  CreateSessionRequest,
  Session,
  GetVenuesResponse,
  EscCalenderData,
  CommonFilterParams,
  EscCalenderSingleValue,
  UpdateCalenderRequest,
  uploadImage,
  guestData,
  singleGuestValue,
  FilterParams,
} from '../types';
import { DashboardMetricsResponse } from '../types/dashboard.types';
import { viewerRegisterType } from '../components/modals/VisitorsEditDialog';
import { viewer } from '../types/user.type';
import { ChangePasswordForm } from '../components/modals/profile/ChangePasswordDialog';
import { profileData } from '../types/profile.type';
import { UserFormData } from '../components/modals/UserEditDialog';
import { FormDataType } from '../components/modals/EventEditDialog';
import { toast } from 'react-toastify';
import { SendNotificationParams } from '../types/notificationSend.type';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      // baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000',
      baseURL:
        process.env.REACT_APP_API_URL ||
        'https://adminindiasoft.intellexio.com/api/v1',
      timeout: 10000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.api.interceptors.request.use(config => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      response => response,
      error => {
        if (
          error.response?.status === 401 &&
          !error.config.url?.includes('/auth/login')
        ) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private handleSuccess<T>(response: any): T {
    toast.success(response.message);

    return response;
  }

  private handleError(error: any): never {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Something went wrong';

    toast.error(message);
    throw error;
  }

  private async request<T>(promise: Promise<any>): Promise<T> {
    try {
      const { data } = await promise;
      return this.handleSuccess<T>(data);
    } catch (error) {
      this.handleError(error);
    }
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // return this.request<LoginResponse>(
    //   this.api.post('/auth/login', credentials)
    // );

    const response = await this.api.post<LoginResponse>(
      '/auth/login',
      credentials
    );
    return response.data;
  }

  // async getUsers(params?: UserFilterParams): Promise<PaginatedResponse<User>> {
  //   return this.request<PaginatedResponse<User>>(
  //     this.api.get('/admin/users', { params })
  //   );
  // }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.api.get<DashboardStats>('/admin/dashboard');
    return response.data;
  }

  // Dashboard Metrics (All Metrics)
  async getAllMetrics(): Promise<DashboardMetricsResponse> {
    const response =
      await this.api.get<DashboardMetricsResponse>('/admin/metrics/all');
    return response.data;
  }

  // User Management
  async getUsers(params?: UserFilterParams): Promise<PaginatedResponse<User>> {
    const response = await this.api.get<PaginatedResponse<User>>(
      '/admin/users',
      { params }
    );
    return response.data;
  }

  async getDelegates(
    params?: UserFilterParams
  ): Promise<PaginatedResponse<User>> {
    const response = await this.api.get<PaginatedResponse<User>>(
      '/admin/users',
      { params }
    );
    return response.data;
  }

  async getUserById(id: number): Promise<User> {
    const response = await this.api.get<User>(`/admin/users/${id}`);
    return response.data;
  }

  async updateUser(id: number, data: UpdateUserRequest): Promise<User> {
    const response = await this.api.put<User>(`/admin/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: number): Promise<void> {
    await this.api.delete(`/admin/users/${id}`);
  }

  async toggleUserStatus(id: number): Promise<User> {
    const response = await this.api.patch<User>(
      `/admin/users/${id}/toggle-status`
    );
    return response.data;
  }

  async bulkUpdateUsers(data: BulkUpdateRequest): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>(
      '/admin/users/bulk-update',
      data
    );
    return response.data;
  }

  async exportUsers(params?: UserFilterParams): Promise<ApiResponse<any[]>> {
    const response = await this.api.get<ApiResponse<any[]>>(
      '/admin/users/export',
      { params }
    );
    return response.data;
  }

  // Event Management
  async getEvents(
    params?: EventFilterParams
  ): Promise<PaginatedResponse<Event>> {
    const response = await this.api.get<PaginatedResponse<Event>>(
      '/admin/events',
      { params }
    );
    return response.data;
  }

  async createEvent(data: FormDataType): Promise<Event> {
    const response = await this.api.post<Event>('/events', data);
    return response.data;
  }

  async updateEvent(id: number, data: FormDataType): Promise<Event> {
    const response = await this.api.put<Event>(`/events/${id}`, data);
    return response.data;
  }

  async deleteEvent(id: number): Promise<void> {
    await this.api.delete(`/admin/events/${id}`);
  }

  async toggleEventStatus(id: number): Promise<Event> {
    const response = await this.api.patch<Event>(
      `/admin/events/${id}/toggle-status`
    );
    return response.data;
  }

  // Exhibitor Management
  // async getExhibitors(
  //   params?: ExhibitorFilterParams
  // ): Promise<PaginatedResponse<ExhibitorDetails>> {
  //   const response = await this.api.get<PaginatedResponse<ExhibitorDetails>>(
  //     '/admin/exhibitors',
  //     { params }
  //   );
  //   return response.data;
  // }

  async getExhibitors(filters: ExhibitorFilterParams) {
    const params = new URLSearchParams();

    params.append('page', String(filters.page));
    params.append('limit', String(filters.limit));

    if (filters.searchTerm) {
      params.append('searchTerm', filters.searchTerm);
    }

    if (filters.isActive !== undefined) {
      params.append('isActive', String(filters.isActive));
    }

    if (filters.categoryId && filters.categoryId.length > 0) {
      filters.categoryId.forEach(id => {
        params.append('categoryIds', String(id));
      });
    }

    if (filters.isVerified !== undefined) {
      params.append('isVerified', String(filters.isVerified));
    }

    const response = await this.api.get<PaginatedResponse<ExhibitorDetails>>(
      '/admin/exhibitors',
      { params }
    );
    return response.data;
  }

  async getExhibitorStats(): Promise<any[]> {
    const response = await this.api.get<any[]>('/admin/exhibitors/stats');
    return response.data;
  }

  async getExhibitorById(id: number): Promise<ExhibitorDetails> {
    const response = await this.api.get<ExhibitorDetails>(
      `/admin/exhibitors/${id}`
    );
    return response.data;
  }

  async updateExhibitor(id: number, data: any): Promise<ExhibitorDetails> {
    const response = await this.api.put<ExhibitorDetails>(
      `/admin/exhibitors/${id}`,
      data
    );
    return response.data;
  }

  async deleteExhibitor(id: number): Promise<void> {
    await this.api.delete(`/admin/exhibitors/${id}`);
  }

  async exportExhibitors(
    params?: ExhibitorFilterParams
  ): Promise<ApiResponse<any[]>> {
    const response = await this.api.get<ApiResponse<any[]>>(
      '/admin/exhibitors/export',
      { params }
    );
    return response.data;
  }

  // Role Management
  async getRoles(): Promise<Role[]> {
    const response = await this.api.get<Role[]>('/admin/roles');
    return response.data;
  }

  async getRole(id: number): Promise<Role> {
    const response = await this.api.get<Role>(`/admin/roles/${id}`);
    return response.data;
  }

  async createRole(data: CreateRoleRequest): Promise<Role> {
    const response = await this.api.post<Role>('/admin/roles', data);
    return response.data;
  }

  async updateRole(id: number, data: UpdateRoleRequest): Promise<Role> {
    const response = await this.api.put<Role>(`/admin/roles/${id}`, data);
    return response.data;
  }

  async deleteRole(id: number): Promise<void> {
    await this.api.delete(`/admin/roles/${id}`);
  }

  async getPermissions(): Promise<Permission[]> {
    const response = await this.api.get<Permission[]>(
      '/admin/roles/permissions'
    );
    return response.data;
  }

  async assignRoleToUser(
    userId: number,
    data: AssignRoleRequest
  ): Promise<ApiResponse<any>> {
    const response = await this.api.post<ApiResponse<any>>(
      `/admin/roles/users/${userId}/assign`,
      data
    );
    return response.data;
  }

  async removeRoleFromUser(userId: number): Promise<ApiResponse<any>> {
    const response = await this.api.delete<ApiResponse<any>>(
      `/admin/roles/users/${userId}/remove`
    );
    return response.data;
  }

  async getUsersByRole(roleId: number): Promise<ApiResponse<User[]>> {
    const response = await this.api.get<ApiResponse<User[]>>(
      `/admin/roles/${roleId}/users`
    );
    return response.data;
  }

  // Guest Speaker Management
  async getGuestSpeakers(params?: {
    searchTerm?: string;
    isActive?: boolean;
    isKeynoteSpeaker?: boolean;
    isFeaturedSpeaker?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<GuestSpeaker>> {
    const response = await this.api.get<PaginatedResponse<GuestSpeaker>>(
      // '/admin/speakers',
      '/speakers',
      { params }
    );
    return response.data;
  }

  async getGuestSpeaker(id: number): Promise<GuestSpeaker> {
    const response = await this.api.get<GuestSpeaker>(`/speakers/${id}`);
    return response.data;
  }

  async createGuestSpeaker(data: Partial<GuestSpeaker>): Promise<GuestSpeaker> {
    const response = await this.api.post<GuestSpeaker>('/speakers', data);
    return response.data;
  }

  async updateGuestSpeaker(
    id: number,
    data: Partial<GuestSpeaker>
  ): Promise<GuestSpeaker> {
    const response = await this.api.put<GuestSpeaker>(`/speakers/${id}`, data);
    return response.data;
  }

  async deleteGuestSpeaker(id: number): Promise<void> {
    await this.api.delete(`/speakers/${id}`);
  }

  async toggleGuestSpeakerStatus(id: number): Promise<GuestSpeaker> {
    const response = await this.api.put<GuestSpeaker>(
      `/speakers/${id}/toggle-status`
    );
    return response.data;
  }

  async toggleGuestSpeakerFeatured(id: number): Promise<GuestSpeaker> {
    const response = await this.api.put<GuestSpeaker>(
      `/speakers/${id}/toggle-featured`
    );
    return response.data;
  }

  async toggleGuestSpeakerKeynote(id: number): Promise<GuestSpeaker> {
    const response = await this.api.put<GuestSpeaker>(
      `/speakers/${id}/toggle-keynote`
    );
    return response.data;
  }

  ///////////////TODO: get all session////////////////
  async getSessions(
    params?: CommonFilterParams
  ): Promise<PaginatedResponse<Session>> {
    const response = await this.api.get<PaginatedResponse<Session>>(
      '/sessions',
      { params }
    );
    return response.data;
  }

  ///////////////TODO: create session api///////////
  async createSession(data: CreateSessionRequest): Promise<Session> {
    const response = await this.api.post<Session>('/sessions', data);
    return response.data;
  }

  /////////////TODO: update session api///////////////
  async updateSession(
    id: number,
    data: CreateSessionRequest
  ): Promise<Session> {
    const response = await this.api.put<Session>(`/sessions/${id}`, data);
    return response.data;
  }
  ///////////////TODO: delete session api/////////////
  async deleteSession(id: number): Promise<void> {
    await this.api.delete(`/sessions/${id}`);
  }

  async toggleSessionStatus(id: number): Promise<Session> {
    const response = await this.api.patch<Session>(
      `/sessions/${id}/toggle-status`
    );
    return response.data;
  }

  //////////////////TODO: collect venue details////////////
  async getVenues(): Promise<GetVenuesResponse> {
    const response = await this.api.get(`/floor`);
    return response.data;
  }
  ///////////////TODO:upload file///////////////
  async uploadSpeakerImage(formData: FormData): Promise<uploadImage> {
    const response = await this.api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  ///////////////////TODO: Delete file api///////////////////
  async deleteSpeakerImage(url: string) {
    await this.api.delete(`/files?url=${encodeURIComponent(url)}`);
  }

  ////////////////////TODO: esc calender api///////////////////
  async getEscCalender(
    page: number = 1,
    limit: number = 25
  ): Promise<PaginatedResponse<EscCalenderSingleValue>> {
    const response = await this.api.get<
      PaginatedResponse<EscCalenderSingleValue>
    >(`/esc-calender?page=${page}&limit=${limit}`);

    return response.data;
  }

  //////////////TODO: create esc calender//////////////
  async createEscCalender(
    data: EscCalenderData
  ): Promise<EscCalenderSingleValue> {
    const response = await this.api.post<EscCalenderSingleValue>(
      '/esc-calender',
      data
    );
    return response.data;
  }

  //////////////////TODO: update esc calender//////////////
  async updateEscCalender(
    id: number,
    data: UpdateCalenderRequest
  ): Promise<EscCalenderSingleValue> {
    const response = await this.api.put<EscCalenderSingleValue>(
      `/esc-calender/${id}`,
      data
    );
    return response.data;
  }

  //////////////TODO: delete esc calender//////////////////
  async deleteEscCalender(id: number): Promise<void> {
    await this.api.delete(`/esc-calender/${id}`);
  }

  ////////////////TODO: upload floor image///////////////////////
  async uploadFloorImage(formData: FormData): Promise<uploadImage> {
    const response = await this.api.post('/floor/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  ////////////////////TODO: viewer create////////////////////
  async createViewer(data: viewerRegisterType): Promise<viewer> {
    const response = await this.api.post<viewer>('/auth/register', data);
    return response.data;
  }

  //////////////////TODO: Fetch profile///////////
  async changePassword(data: ChangePasswordForm): Promise<any> {
    const response = await this.api.post(`/auth/change-password`, data);
    return response.data;
  }

  //////////////////TODO: Create User /////////////////
  async createUser(data: UserFormData): Promise<any> {
    const response = await this.api.post(`/auth/create`, data);
    return response.data;
  }

  //////////////////TODO:Update profile///////////
  async updateProfile({
    firstName,
    lastName,
    phone,
  }: {
    firstName: string;
    lastName: string;
    phone: string;
  }): Promise<profileData> {
    const response = await this.api.patch<profileData>(`/auth/update-profile`, {
      firstName,
      lastName,
      phone,
    });
    return response.data;
  }

  //////////////guest list/////////
  async getGuests(
    params?: EventFilterParams
  ): Promise<PaginatedResponse<singleGuestValue>> {
    const response = await this.api.get<PaginatedResponse<singleGuestValue>>(
      '/events/guest/list',
      { params }
    );
    return response.data;
  }
  ////////////delete guest///////////
  async deleteGuest(id: number): Promise<void> {
    await this.api.delete(`/events/guest/${id}`);
  }

  ///////////////TODO: Create guest//////////////////
  async createGuest(formData: FormData): Promise<guestData> {
    const response = await this.api.post('/events/guest', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  //////////update guest/////////////
  async updateGuest(id: number, data: FormData): Promise<Session> {
    const response = await this.api.patch<Session>(`/events/guest/${id}`, data);
    return response.data;
  }

  /////////////send otp///////////////
  async sendOtpToEmail({ email }: { email: string }) {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  //////////////////////////resend otp//////////////
  async resendOtp({ email }: { email: string }) {
    const response = await this.api.post('/auth/resend-otp', { email });
    return response.data;
  }
  /////////////verify otp///////////////
  async verifyOtp(params: { email: string; otpCode: string; otpType: string }) {
    const response = await this.api.post('/auth/verify-otp', params);
    return response.data;
  }

  ///////////////reset password//////////////
  async passwordReset(params: {
    email: string;
    resetToken: string;
    newPassword: string;
  }) {
    const response = await this.api.post('/auth/reset-password', params);
    return response.data;
  }

  /////////////////////notification list////////////////
  async getAllNotifications(params?: FilterParams) {
    const response = await this.api.get('/notifications', { params });
    return response.data;
  }

  ////////////////////////approveExhibitor///////////////////
  async approveExhibitor(
    id: number,
    params: {
      boothNumber: string;
      boothType: 'shell_space' | 'raw_space';
    }
  ) {
    const response = await this.api.post(
      `/admin/exhibitors/${id}/approve`,
      params
    );
    return response.data;
  }

  ////////////////////////Update booth number///////////////////
  async updateExhibitorBooth(
    id: number,
    params: {
      boothNumber: string;
      boothType: 'shell_space' | 'raw_space';
    }
  ) {
    const response = await this.api.patch(
      `/admin/exhibitors/${id}/update-booth-number`,
      params
    );
    return response.data;
  }

  ///////////////////////fetch category/////////////////
  async getAllCategory() {
    const response = await this.api.get('/exhibitors/categories');
    return response.data;
  }

  ///////////////////////fetch category/////////////////
  async getAllQuery(params?: {
    page?: number;
    limit?: number;
    searchTerm?: string;
    queryType?: string;
    email?: string;
  }) {
    const response = await this.api.get('/query', {
      params,
    });

    return response.data;
  }

  ///////////////////////////getmeeting//////////////////
  async getMeetings(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    dayCode?: string;
  }) {
    const response = await this.api.get('/admin/meetings', {
      params,
    });

    return response.data;
  }

  ///////////////////////////send notification//////////////////
  async sendNotification(params: SendNotificationParams) {
    const response = await this.api.post(
      '/all-notification/admin/send',
      params
    );

    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
